import React, { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { Bookmark } from 'lucide-react';
import { savePost, unsavePost } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { CommunityError } from "../../errors/sentryErrors";
import { trackEvent } from "../../utils/analytics";

const SaveButton = ({ post, className = "", variant = "default" }) => {
  const { user, isBanned } = useAuth();
  
  // Track optimistic state
  const [saved, setSaved] = useState(post?.isSavedByViewer || false);
  const [count, setCount] = useState(post?.counters?.saveCount || 0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSaved(post?.isSavedByViewer || false);
    setCount(post?.counters?.saveCount || 0);
  }, [post]);

  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error('Please log in to save this trip.');
      return;
    }
    if (isBanned) return;
    if (loading) return;

    // Optimistic Update
    const newSaved = !saved;
    const newCount = newSaved ? count + 1 : Math.max(0, count - 1);
    
    setSaved(newSaved);
    setCount(newCount);
    setLoading(true);

    if (newSaved) {
      toast.success('Saved to your collection', { icon: '🔖', autoClose: 2000 });
    }

    try {
      if (newSaved) {
        await savePost(post.id);
      } else {
        await unsavePost(post.id);
      }
      trackEvent(newSaved ? "post_saved" : "post_unsaved", { post_id: post.id, variant });
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      // Rollback on error
      setSaved(!newSaved);
      setCount(!newSaved ? newCount + 1 : Math.max(0, newCount - 1));
      
      if (error.status === 429) {
        toast.error(`Slow down. Try again in ${error.retryAfter || 'a few'} seconds.`);
      } else if (error.status === 403 && error.code === 'SAVE_LIMIT_REACHED') {
        toast.error("You've reached the free tier limit of 100 saved itineraries. Upgrade to Pro.", {
          className: "bg-blue-50 text-blue-900 border border-blue-200"
        });
      } else {
        toast.error('Failed to update save. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (variant === "large") {
    return (
      <div 
        className={`relative border border-gray-100 rounded-xl p-3 flex flex-col items-center justify-center gap-1.5 hover:border-blue-200 hover:bg-blue-50 transition-colors cursor-pointer text-center group ${isBanned ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        onClick={isBanned ? undefined : handleToggle}
      >
        <div className={`transition-transform duration-300 group-hover:scale-110 ${saved ? 'text-blue-500' : 'text-blue-500'}`}>
          <Bookmark size={18} className={saved ? 'fill-blue-500' : ''} />
        </div>
        <div>
          <div className="font-bold text-gray-900 text-sm mb-0">{count.toLocaleString()}</div>
          <div className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Saves</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button
        onClick={handleToggle}
        disabled={isBanned}
        className={`${isBanned ? 'opacity-50 cursor-not-allowed ' : ''}p-2 rounded-full hover:bg-gray-100 transition-all duration-300 active:scale-90 flex items-center justify-center`}
        aria-label="Save post"
      >
        <Bookmark 
          size={20} 
          className={`transition-colors duration-300 ${saved ? 'fill-blue-600 text-blue-600' : 'text-gray-600'}`} 
        />
      </button>
      {count > 0 && (
        <span className="text-sm font-medium text-gray-600">{count}</span>
      )}
    </div>
  );
};

export default SaveButton;
